"use client";

import { useState, useEffect } from "react";
import { useAppSelector } from "@/hooks/hooks";
import { Calendar, ChevronRight, ChevronLeft } from "lucide-react";
import Slider from "react-slick";
import "../styles/carousel.css";

const styleCycles = [
  {
    color: "text-pink-600",
    border: "border-l-pink-500",
    bg: "bg-gradient-to-br from-pink-50/50 via-white to-pink-100/10 hover:from-pink-50/70 hover:via-white hover:to-pink-100/25",
    bullet: "bg-pink-500",
    iconBg: "bg-pink-50 text-pink-600",
    scrollbarColor: "#ec4899",
    borderColor: "border-pink-100/50",
  },
  {
    color: "text-blue-600",
    border: "border-l-blue-500",
    bg: "bg-gradient-to-br from-blue-50/50 via-white to-blue-100/10 hover:from-blue-50/70 hover:via-white hover:to-blue-100/25",
    bullet: "bg-blue-500",
    iconBg: "bg-blue-50 text-blue-600",
    scrollbarColor: "#3b82f6",
    borderColor: "border-blue-100/50",
  },
  {
    color: "text-purple-600",
    border: "border-l-purple-500",
    bg: "bg-gradient-to-br from-purple-50/50 via-white to-purple-100/10 hover:from-purple-50/70 hover:via-white hover:to-purple-100/25",
    bullet: "bg-purple-500",
    iconBg: "bg-purple-50 text-purple-600",
    scrollbarColor: "#a855f7",
    borderColor: "border-purple-100/50",
  },
  {
    color: "text-orange-600",
    border: "border-l-orange-500",
    bg: "bg-gradient-to-br from-orange-50/50 via-white to-orange-100/10 hover:from-orange-50/70 hover:via-white hover:to-orange-100/25",
    bullet: "bg-orange-500",
    iconBg: "bg-orange-50 text-orange-600",
    scrollbarColor: "#f97316",
    borderColor: "border-orange-100/50",
  },
  {
    color: "text-red-600",
    border: "border-l-red-500",
    bg: "bg-gradient-to-br from-red-50/50 via-white to-red-100/10 hover:from-red-50/70 hover:via-white hover:to-red-100/25",
    bullet: "bg-red-500",
    iconBg: "bg-red-50 text-red-600",
    scrollbarColor: "#ef4444",
    borderColor: "border-red-100/50",
  },
  {
    color: "text-green-600",
    border: "border-l-green-500",
    bg: "bg-gradient-to-br from-green-50/50 via-white to-green-100/10 hover:from-green-50/70 hover:via-white hover:to-green-100/25",
    bullet: "bg-green-500",
    iconBg: "bg-green-50 text-green-600",
    scrollbarColor: "#22c55e",
    borderColor: "border-green-100/50",
  },
];

// Slider Arrows matching home page testimonials
function NextArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Next slide"
    >
      <ChevronRight className="w-5 h-5" />
    </button>
  );
}

function PrevArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Previous slide"
    >
      <ChevronLeft className="w-5 h-5" />
    </button>
  );
}

export default function SpecialDatesNew({ cmsData }: { cmsData?: any }) {
  const { homeDetails } = useAppSelector((state) => state.home);
  const specialDateList = homeDetails?.SpecialDateList ?? [];

  // Dynamic slidesCount — reads actual window.innerWidth
  const getSlidesToShow = () => {
    if (typeof window === "undefined") return 3;
    const w = window.innerWidth;
    if (w <= 640) return 1;
    if (w <= 1024) return 2;
    return 3;
  };
  const [slidesCount, setSlidesCount] = useState(getSlidesToShow);
  useEffect(() => {
    const onResize = () => setSlidesCount(getSlidesToShow());
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const displayEvents =
    specialDateList.length > 0
      ? specialDateList.map((item: any, index: number) => {
          const events = item.SpecialDates
            ? item.SpecialDates.map((d: any) => ({
                DateTitle: d.DateTitle,
                Description: d.Description,
              }))
            : [];

          const style = styleCycles[index % styleCycles.length];

          return {
            monthYear: item.MonthYear || "Special Date",
            events: events,
            style: style,
          };
        })
      : [];

  if (displayEvents.length === 0) {
    return null;
  }

  const showArrows = slidesCount > 1 && displayEvents.length > 1;

  const sliderSettings = {
    dots: true,
    infinite: displayEvents.length > slidesCount,
    speed: 500,
    slidesToShow: slidesCount,
    slidesToScroll: 1,
    autoplay: displayEvents.length > slidesCount,
    autoplaySpeed: 5000,
    swipe: true,
    touchMove: true,
    arrows: showArrows,
    nextArrow: showArrows ? <NextArrow /> : undefined,
    prevArrow: showArrows ? <PrevArrow /> : undefined,
  };

  return (
    <section id="special-dates" className="py-20 bg-gray-50/30">
      <div className="max-w-7xl mx-auto px-4">
        {cmsData?.Template ? (
          <div
            className="text-center mb-12"
            dangerouslySetInnerHTML={{ __html: cmsData.Template }}
          />
        ) : (
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl mb-4 font-sans font-semibold text-gray-900">
              Special{" "}
              <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 bg-clip-text text-transparent">
                Dates
              </span>{" "}
              & Occasions
            </h2>
            <p className="text-xl text-gray-600 font-sans">
              Plan Your Magical Desert Visit Around These Occasions
            </p>
          </div>
        )}

        {displayEvents.length > 1 ? (
          <div className="relative px-0 sm:px-8 select-text overflow-visible">
            <Slider {...sliderSettings}>
              {displayEvents.map((event: any, index: number) => (
                <div key={index} className="px-1 sm:px-3 pb-4">
                  <div
                    style={
                      {
                        "--scrollbar-thumb": event.style.scrollbarColor,
                      } as React.CSSProperties
                    }
                    className={`group relative ${event.style.bg} rounded-[24px] p-5 border-l-[6px] ${event.style.border} border border-gray-200/70 hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)] hover:scale-[1.02] transition-all duration-500 flex flex-col h-full justify-between min-h-[250px] overflow-visible`}
                  >
                    <div className="flex flex-col h-full justify-between">
                      {/* Month Header */}
                      <div className="flex items-center gap-3 pb-1">
                        <div
                          className={`w-9 h-9 ${event.style.iconBg} rounded-xl flex items-center justify-center shadow-sm group-hover:rotate-12 group-hover:scale-110 group-hover:shadow-md transition-all duration-300`}
                        >
                          <Calendar className="w-4.5 h-4.5" />
                        </div>
                        <h3
                          className={`text-lg font-sans font-semibold ${event.style.color} whitespace-nowrap leading-snug tracking-tight group-hover:translate-x-0.5 transition-transform duration-300`}
                        >
                          {event.monthYear}
                        </h3>
                      </div>

                      {/* Ticket Divider Line with Left & Right Cutouts */}
                      <div className="relative my-3 mx-[-20px] flex items-center h-5 overflow-visible">
                        {/* Dashed Line */}
                        <div
                          className={`border-t-2 border-dashed ${event.style.borderColor.replace("/50", "/80")} opacity-60 w-full`}
                        ></div>
                        {/* Left ticket cutout */}
                        <div className="absolute -left-[10px] w-5 h-5 bg-[#fafafa] rounded-full border border-gray-200/80 z-10 shadow-[inset_-2px_0_3px_rgba(0,0,0,0.02)]"></div>
                        {/* Right ticket cutout */}
                        <div className="absolute -right-[10px] w-5 h-5 bg-[#fafafa] rounded-full border border-gray-200/80 z-10 shadow-[inset_2px_0_3px_rgba(0,0,0,0.02)]"></div>
                      </div>

                      {/* Date List Container (Fixed Height + Scrollable) */}
                      <div className="max-h-[140px] overflow-y-auto pr-1 custom-scrollbar flex-grow">
                        <ul className="space-y-3.5">
                          {event.events.map((date: any, i: number) => (
                            <li
                              key={i}
                              className="flex items-start gap-2.5 group/item"
                            >
                              <span
                                className={`flex-shrink-0 w-1.5 h-1.5 rotate-45 ${event.style.bullet} mt-2 shadow-sm group-hover/item:scale-125 group-hover/item:rotate-[135deg] transition-all duration-300`}
                              ></span>
                              <span className="text-sm text-gray-700 font-sans font-medium leading-relaxed group-hover/item:text-gray-900 transition-colors duration-200">
                                {date.DateTitle}
                                {date.Description
                                  ? ` - ${date.Description}`
                                  : ""}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 select-text">
            {displayEvents.map((event: any, index: number) => (
              <div
                key={index}
                style={
                  {
                    "--scrollbar-thumb": event.style.scrollbarColor,
                  } as React.CSSProperties
                }
                className={`group relative ${event.style.bg} rounded-[24px] p-5 border-l-[6px] ${event.style.border} border border-gray-200/70 hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)] hover:scale-[1.03] transition-all duration-500 flex flex-col h-full justify-between min-h-[250px] overflow-visible`}
              >
                <div className="flex flex-col h-full justify-between">
                  {/* Month Header */}
                  <div className="flex items-center gap-3 pb-1">
                    <div
                      className={`w-9 h-9 ${event.style.iconBg} rounded-xl flex items-center justify-center shadow-sm group-hover:rotate-12 group-hover:scale-110 group-hover:shadow-md transition-all duration-300`}
                    >
                      <Calendar className="w-4.5 h-4.5" />
                    </div>
                    <h3
                      className={`text-lg font-sans font-semibold ${event.style.color} whitespace-nowrap leading-snug tracking-tight group-hover:translate-x-0.5 transition-transform duration-300`}
                    >
                      {event.monthYear}
                    </h3>
                  </div>

                  {/* Ticket Divider Line with Left & Right Cutouts */}
                  <div className="relative my-3 mx-[-20px] flex items-center h-5 overflow-visible">
                    {/* Dashed Line */}
                    <div
                      className={`border-t-2 border-dashed ${event.style.borderColor.replace("/50", "/80")} opacity-60 w-full`}
                    ></div>
                    {/* Left ticket cutout */}
                    <div className="absolute -left-[10px] w-5 h-5 bg-[#fafafa] rounded-full border border-gray-200/80 z-10 shadow-[inset_-2px_0_3px_rgba(0,0,0,0.02)]"></div>
                    {/* Right ticket cutout */}
                    <div className="absolute -right-[10px] w-5 h-5 bg-[#fafafa] rounded-full border border-gray-200/80 z-10 shadow-[inset_2px_0_3px_rgba(0,0,0,0.02)]"></div>
                  </div>

                  {/* Date List Container (Fixed Height + Scrollable) */}
                  <div className="max-h-[140px] overflow-y-auto pr-1 custom-scrollbar flex-grow">
                    <ul className="space-y-3.5">
                      {event.events.map((date: any, i: number) => (
                        <li
                          key={i}
                          className="flex items-start gap-2.5 group/item"
                        >
                          <span
                            className={`flex-shrink-0 w-1.5 h-1.5 rotate-45 ${event.style.bullet} mt-2 shadow-sm group-hover/item:scale-125 group-hover/item:rotate-[135deg] transition-all duration-300`}
                          ></span>
                          <span className="text-sm text-gray-700 font-sans font-medium leading-relaxed group-hover/item:text-gray-900 transition-colors duration-200">
                            {date.DateTitle}
                            {date.Description ? ` - ${date.Description}` : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        {/* <div className="mt-12 bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-8 border border-pink-100">
          <div className="flex items-start gap-4">
            <Calendar className="w-8 h-8 text-pink-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl mb-2 text-gray-900 font-sans font-bold">Best Time to Visit Rann Utsav</h3>
              <p className="text-gray-600 font-sans">
                The festival runs from November to February each year. Full moon nights offer the most spectacular views of the white desert.
                Book early for special dates like Diwali, Christmas, New Year, and the International Kite Festival on January 14th.
              </p>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  );
}
